import { useMemo } from 'react';
import type { CanvasEdge, CanvasStandaloneNode } from '../../sweep-canvas/canvas-types';
import { PlatformImgIcon } from '../../common/sf-icons-dialog/SfImgIcon';
import { getOrgTypeFromMetadataId } from '../../pages/funnel-map-flow/metadata-elements/metadataElementUtils';
import { SweepCanvasReactFlowEdgeDataType } from '../../sweep-canvas/edges/SweepCanvasReactFlowEdgeDataType';
import { useGetTopTag } from './useGetTopTag';
import { createEdgeCompositeId, createTargetFunnelLinkCompositeId } from '../helper';
import type { VisibilityMap } from '../../../types/VisibilityTypes';
import { translateTypeToHumanName } from '../../documentation/helpers';
import type { CrmOrg } from '../../../types/CrmOrgs';
import type { FunnelMapMetadataElement } from '../../../types/FunnelMapMetadataElement';
import type { IconPlatform } from '../../common/sf-icons-dialog/types';

// Filters metadata elements:
//  - filters by elements not present in funnelMap.metadataElementsData
//  - filters by elements with missing data. The contract is if they are missing the element was possibly removed from SF.
//  - If the org no longer exists it means the element should not be rendered.

const filterInvalidMetadataElements =
  (funnelMap: FunnelMap, crmOrgs?: CrmOrg[]) =>
  ([id, { crmOrgId, metadataId }]: [string, FunnelMapMetadataElement]) => {
    const metadataElementData = funnelMap.metadataElementsData[id];
    if (!metadataElementData) {
      return false;
    }
    const { type, name } = metadataElementData;
    if (!(type && crmOrgId && name && metadataId)) {
      return false;
    }
    return crmOrgs?.some((crmOrg) => crmOrg.id === crmOrgId) || false;
  };

export const useCalculateMetadataElementStandaloneNodes = ({
  funnelMap,
  crmOrgs,
  visibilityMap,
}: {
  funnelMap: FunnelMap;
  crmOrgs?: CrmOrg[];
  visibilityMap: VisibilityMap;
}) => {
  const getTopTag = useGetTopTag(visibilityMap, crmOrgs);
  return useMemo(() => {
    const edges: CanvasEdge[] = [];
    const standaloneNodes: CanvasStandaloneNode[] = Object.entries(funnelMap.metadataElements || {})
      .filter(filterInvalidMetadataElements(funnelMap, crmOrgs))
      .map(([id, { position, icon, color, funnelLinks, crmOrgId, metadataId, sectionId }]) => {
        const metadataElementData = funnelMap.metadataElementsData[id];
        const crmOrgName = getTopTag(crmOrgId);
        const platform = metadataId
          ? (getOrgTypeFromMetadataId(metadataId) as IconPlatform)
          : 'salesforce';

        funnelLinks.forEach((link) => {
          const target = createTargetFunnelLinkCompositeId(link);
          edges.push({
            id: createEdgeCompositeId(id, target),
            source: id,
            target,
            data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
          });
        });

        return {
          id,
          nodeType: 'standalone',
          name: metadataElementData.name,
          type: translateTypeToHumanName(metadataElementData.type),
          topTag: crmOrgName,
          position,
          icon: icon ? (
            <PlatformImgIcon imageName={icon} platform={platform} width="16px" height="16px" />
          ) : undefined,
          color,
          sectionId,
        };
      });
    return { standaloneNodes, edges };
  }, [funnelMap, crmOrgs, getTopTag]);
};
